import { useUIStore, useMovieStore, useAuthStore } from '../../../stores';
import { TrashIcon } from '@radix-ui/react-icons';
import { Button } from '@radix-ui/themes';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useSignalR } from '../../../hooks';
import { useTranslation } from '../../../i18n';
import {
  fetchVectorPackageFiles,
  fetchVectorPackages,
  importVectorPackageFile,
  resolveAssetUrl,
  uploadFile,
  type LibraryObjectUploadResponse,
  type VectorPackageFile,
  type VectorPackageSummary,
} from '../../../util/api';
import { extractAssetPathFromBody } from '../../../util/imageBody';
import type { LibraryItem, SceneObject, LibraryObjectResponse, SceneObjectResponse } from '../../../types';
import { Toolpad } from '../Toolpad';
import { ConfirmModal } from '../../ui/ConfirmModal';

export function Library() {
  const { t, locale } = useTranslation();
  const { toolpads, toggleToolpad } = useUIStore();
  const {
    library,
    addLibraryItem,
    deleteLibraryItem,
    currentScene,
    currentMovie,
    currentMovieResponse,
    currentLayer,
    addSceneObject
  } = useMovieStore();
  const {
    status: signalRStatus,
    connect,
    sceneObjectCreateFromLibraryItem,
    sceneObjectsGetByLayer,
    libraryObjectDelete
  } = useSignalR();
  const { user, accessToken } = useAuthStore();
  const isOpen = toolpads.library.isOpen;
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteCandidate, setDeleteCandidate] = useState<LibraryItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [usageWarning, setUsageWarning] = useState<{ name: string; usageCount: number } | null>(null);
  const [layerSelectionWarningOpen, setLayerSelectionWarningOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef(false);
  const [librarySource, setLibrarySource] = useState<string>('mine');
  const [vectorPackages, setVectorPackages] = useState<VectorPackageSummary[]>([]);
  const [vectorFiles, setVectorFiles] = useState<VectorPackageFile[]>([]);
  const [vectorsLoading, setVectorsLoading] = useState(false);
  const [vectorError, setVectorError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    fetchVectorPackages(accessToken)
      .then((list) => {
        if (!cancelled) setVectorPackages(list);
      })
      .catch(() => {
        if (!cancelled) setVectorError(t('toolpads.library.vectorsLoadFailed'));
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, accessToken, locale]);

  useEffect(() => {
    if (librarySource === 'mine') return;
    if (vectorPackages.length > 0 && !vectorPackages.some((pkg) => pkg.id === librarySource)) {
      setLibrarySource('mine');
    }
  }, [librarySource, vectorPackages]);

  useEffect(() => {
    if (!isOpen || librarySource === 'mine') return;
    let cancelled = false;
    setVectorsLoading(true);
    setVectorError(null);
    fetchVectorPackageFiles(librarySource, accessToken)
      .then((files) => {
        if (!cancelled) setVectorFiles(files);
      })
      .catch(() => {
        if (!cancelled) {
          setVectorFiles([]);
          setVectorError(t('toolpads.library.vectorsLoadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setVectorsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, librarySource, accessToken, locale]);

  const resolveTargetLayerId = useCallback((): number | null => {
    const fromSelection = currentLayer ? Number(currentLayer.id) : NaN;
    if (!Number.isNaN(fromSelection) && fromSelection > 0) {
      return fromSelection;
    }
    return null;
  }, [currentLayer]);

  const normalizeObjectSize = useCallback((value: number | undefined): number => {
    return typeof value === 'number' && value > 0 ? Math.round(value) : 100;
  }, []);

  const mapSceneObjectResponseToStore = useCallback((response: SceneObjectResponse): SceneObject => {
    let props: Record<string, number> = {};
    if (response.defaultState?.properties) {
      try {
        props = JSON.parse(response.defaultState.properties);
      } catch {
        // ignore invalid json
      }
    }

    const rawSourcePath =
      response.url ?? response.fullPath ?? extractAssetPathFromBody(response.body);
    const resolvedSrc = rawSourcePath ? resolveAssetUrl(rawSourcePath) : undefined;

    const x = Math.round(props.left ?? props.x ?? response.dropPosition?.left ?? 0);
    const y = Math.round(props.top ?? props.y ?? response.dropPosition?.top ?? 0);
    const width = normalizeObjectSize(props.width ?? response.width);
    const height = normalizeObjectSize(props.height ?? response.height);
    const rotation = props.rotation ?? 0;
    const opacity = props.opacity ?? 1;

    return {
      id: String(response.id),
      type: (response.type as SceneObject['type']) || 'image',
      name: response.name,
      src: resolvedSrc || undefined,
      libraryObjectId: response.libraryObjectId ?? undefined,
      parentObjectId: response.parentObjectId != null ? String(response.parentObjectId) : null,
      isGroup: response.isGroup === true || response.type === 'group',
      x,
      y,
      width,
      height,
      rotation,
      opacity,
      zIndex: response.sorter ?? currentScene?.objects.length ?? 0,
      visible: response.isVisible,
      locked: response.isLocked,
      pivotPoint: response.pivotPoint?.trim() ? response.pivotPoint : 'center',
      layerId: response.layerId,
      defaultPose: { x, y, width, height, rotation, opacity },
      states: [],
    };
  }, [currentScene?.objects.length, normalizeObjectSize]);

  const hasLibraryItemDrag = (e: React.DragEvent) => {
    const types = Array.from(e.dataTransfer.types ?? []);
    return types.includes('application/x-prototype-library-item') || types.includes('application/json');
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only show drag-over for file uploads, not when dragging a library item out
    if (hasLibraryItemDrag(e)) return;
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const uploadImages = useCallback(async (files: File[]) => {
    if (!currentMovie) {
      console.error('No movie selected');
      return;
    }

    if (!user?.id) {
      console.error('User not authenticated');
      return;
    }

    const images = files.filter((file) => file.type.startsWith('image/'));
    if (images.length === 0 || uploadingRef.current) return;

    uploadingRef.current = true;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      for (let index = 0; index < images.length; index += 1) {
        const file = images[index];
        try {
          const uploaded = await uploadFile(file, parseInt(currentMovie.id, 10), (progress) => {
            const overall = ((index + progress / 100) / images.length) * 100;
            setUploadProgress(Math.round(overall));
          }, accessToken);

          const newItem: LibraryItem = {
            id: String(uploaded.id),
            name: uploaded.name || file.name.replace(/\.[^/.]+$/, ''),
            type: 'image',
            src: uploaded.url ? resolveAssetUrl(uploaded.url) : '',
            thumbnail: uploaded.thumbnailUrl ? resolveAssetUrl(uploaded.thumbnailUrl) : undefined,
          };
          addLibraryItem(newItem);
        } catch (error) {
          console.error('File upload failed:', error);
        }
      }
    } finally {
      uploadingRef.current = false;
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [addLibraryItem, accessToken, currentMovie, user]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    await uploadImages(Array.from(e.dataTransfer.files));
  }, [uploadImages]);

  const handleImportClick = () => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    await uploadImages(files);
  };

  const libraryItemFromUpload = (uploaded: LibraryObjectUploadResponse, fallbackName: string): LibraryItem => ({
    id: String(uploaded.id),
    name: uploaded.name || fallbackName,
    type: 'image',
    src: uploaded.url ? resolveAssetUrl(uploaded.url) : '',
    thumbnail: uploaded.thumbnailUrl ? resolveAssetUrl(uploaded.thumbnailUrl) : undefined,
  });

  const handleVectorDragStart = (e: React.DragEvent, file: VectorPackageFile) => {
    const payload = JSON.stringify({
      type: 'vectorPackageFile',
      packageId: file.packageId,
      fileId: file.id,
      name: file.name,
      src: resolveAssetUrl(file.url),
      width: file.width,
      height: file.height,
    });
    e.dataTransfer.setData('application/x-prototype-vector-file', payload);
    e.dataTransfer.setData('application/json', payload);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleUseVector = async (file: VectorPackageFile) => {
    if (!currentMovie) return;
    setVectorError(null);
    try {
      const uploaded = await importVectorPackageFile(
        file.packageId,
        file.id,
        Number(currentMovie.id),
        accessToken
      );
      const item = libraryItemFromUpload(uploaded, file.name);
      addLibraryItem(item);
      await handleAddToStage(item);
    } catch (error) {
      console.error('[Library] Failed to import vector package file:', error);
      setVectorError(t('toolpads.library.vectorUseFailed'));
    }
  };

  const handleLibraryItemDragStart = (e: React.DragEvent, item: LibraryItem) => {
    const payload = JSON.stringify({ type: 'libraryItem', item });
    e.dataTransfer.setData('application/x-prototype-library-item', payload);
    e.dataTransfer.setData('application/json', payload);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleAddToStage = async (item: LibraryItem) => {
    if (!currentScene || !currentMovie) return;
    const targetLayerId = resolveTargetLayerId();
    if (!targetLayerId) {
      setLayerSelectionWarningOpen(true);
      return;
    }
    const libraryObjectId = Number(item.id);
    if (!Number.isFinite(libraryObjectId)) {
      console.warn('[Library] Invalid library object id for persisted create', item.id);
      return;
    }
    const centerX = Math.floor(currentMovie.width / 2) - 50;
    const centerY = Math.floor(currentMovie.height / 2) - 50;
    const payload: LibraryObjectResponse = {
      id: libraryObjectId,
      movieId: Number(currentMovie.id),
      name: item.name,
      description: '',
      type: item.type,
      fileName: '',
      width: 100,
      height: 100,
      dropPosition: { top: centerY, left: centerX },
    };
    try {
      if (signalRStatus !== 'Connected') await connect();
      const created = await sceneObjectCreateFromLibraryItem(payload, Number(currentScene.id), targetLayerId);
      const mapped = mapSceneObjectResponseToStore(created);
      if (!mapped.src && item.src) mapped.src = item.src;
      addSceneObject(mapped);
    } catch (error) {
      console.error('[Library] Failed to persist library item on stage:', error);
    }
  };

  const getLibraryItemUsageCount = useCallback(async (libraryObjectId: number): Promise<number> => {
    if (!currentMovieResponse) {
      return (currentScene?.objects ?? []).filter(
        (sceneObject) => sceneObject.libraryObjectId === libraryObjectId
      ).length;
    }

    if (signalRStatus !== 'Connected') {
      await connect();
    }

    let usageCount = 0;
    for (const sceneResponse of currentMovieResponse.scenes) {
      const layeredObjects = await sceneObjectsGetByLayer(sceneResponse);
      for (const layer of layeredObjects) {
        for (const sceneObject of layer.sceneObjects ?? []) {
          if (sceneObject.libraryObjectId === libraryObjectId) {
            usageCount += 1;
          }
        }
      }
    }
    return usageCount;
  }, [connect, currentMovieResponse, currentScene?.objects, sceneObjectsGetByLayer, signalRStatus]);

  const handleDeleteLibraryItem = async (item: LibraryItem) => {
    const libraryObjectId = Number(item.id);
    if (!Number.isFinite(libraryObjectId)) {
      console.warn('[Library] Invalid library object id for delete', item.id);
      return;
    }

    try {
      const usageCount = await getLibraryItemUsageCount(libraryObjectId);
      if (usageCount > 0) {
        setUsageWarning({ name: item.name, usageCount });
        return;
      }
      setDeleteCandidate(item);
    } catch (error) {
      console.error('[Library] Failed to check library item usage before delete:', error);
    }
  };

  const confirmDeleteLibraryItem = async (item: LibraryItem) => {
    if (!currentMovie) return;
    const libraryObjectId = Number(item.id);
    if (!Number.isFinite(libraryObjectId)) {
      console.warn('[Library] Invalid library object id for delete', item.id);
      setDeleteCandidate(null);
      return;
    }
    setDeleteError(null);
    try {
      if (signalRStatus !== 'Connected') {
        await connect();
      }
      await libraryObjectDelete(libraryObjectId);
      deleteLibraryItem(item.id);
    } catch (error) {
      console.error('[Library] Failed to delete library item:', error);
      const raw = error instanceof Error ? error.message : String(error);
      setDeleteError(raw || t('toolpads.library.deleteFailedFallback'));
    } finally {
      setDeleteCandidate(null);
    }
  };

  return (
    <Toolpad
      title={t('toolpads.library.title')}
      isOpen={isOpen}
      onToggle={() => toggleToolpad('library')}
    >
      <ConfirmModal
        open={deleteCandidate !== null}
        onOpenChange={(open) => {
          // Radix kapanisini onClick'ten once isleyebilir; adayi hemen silme (confirm item'i closure'da yakalar).
          if (!open) setTimeout(() => setDeleteCandidate(null), 0);
        }}
        title={t('toolpads.library.deleteConfirmTitle')}
        description={
          deleteCandidate
            ? t('toolpads.library.deleteConfirmDescription', { name: deleteCandidate.name })
            : undefined
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={() => {
          const item = deleteCandidate;
          if (item) void confirmDeleteLibraryItem(item);
        }}
      />
      <ConfirmModal
        open={deleteError !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteError(null);
        }}
        title={t('toolpads.library.deleteFailedTitle')}
        description={deleteError ?? undefined}
        confirmText={t('common.ok')}
        cancelText={t('common.close')}
        onConfirm={() => setDeleteError(null)}
      />
      <ConfirmModal
        open={usageWarning !== null}
        onOpenChange={(open) => {
          if (!open) setUsageWarning(null);
        }}
        title={t('toolpads.library.inUseTitle')}
        description={
          usageWarning
            ? t('toolpads.library.inUseDescription', {
                name: usageWarning.name,
                count: usageWarning.usageCount,
              })
            : undefined
        }
        confirmText={t('common.ok')}
        cancelText={t('common.close')}
        onConfirm={() => setUsageWarning(null)}
      />
      <ConfirmModal
        open={layerSelectionWarningOpen}
        onOpenChange={setLayerSelectionWarningOpen}
        title={t('toolpads.library.noLayerTitle')}
        description={t('toolpads.library.noLayerDescription')}
        confirmText={t('common.ok')}
        cancelText={t('common.close')}
        onConfirm={() => setLayerSelectionWarningOpen(false)}
      />
      <div className="library-source-toolbar" role="toolbar" aria-label={t('toolpads.library.title')}>
        <button
          type="button"
          className={librarySource === 'mine' ? 'active' : ''}
          onClick={() => setLibrarySource('mine')}
        >
          {t('toolpads.library.mine')}
        </button>
        {vectorPackages.map((pkg) => (
          <button
            key={pkg.id}
            type="button"
            className={librarySource === pkg.id ? 'active' : ''}
            title={pkg.name}
            onClick={() => setLibrarySource(pkg.id)}
          >
            {pkg.name}
          </button>
        ))}
      </div>
      {vectorError ? <p className="library-vector-error">{vectorError}</p> : null}
      {librarySource === 'mine' ? (
      <div
        className={`library-content ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
        aria-busy={isUploading}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          aria-label={t('toolpads.library.importAria')}
        />
        {library.length === 0 ? (
          <div className="library-empty">
            <div className="library-drop-zone">
              <p>📷</p>
              <p>{t('toolpads.library.dropImages')}</p>
              <p>{t('common.or')}</p>
              <button className="btn" onClick={handleImportClick} disabled={isUploading}>
                {t('toolpads.library.importFiles')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="library-grid">
              {library.map((item) => (
                <div
                  key={item.id}
                  className="library-item relative"
                  title={item.name}
                  draggable
                  onDragStart={(e) => handleLibraryItemDragStart(e, item)}
                  onDoubleClick={() => handleAddToStage(item)}
                >
                  {item.thumbnail || item.src ? (
                    <img
                      src={item.thumbnail || item.src}
                      alt={item.name}
                      onError={(e) => {
                        const target = e.currentTarget;
                        // Thumbnail yoksa veya zaten ana görsele düştüysek tekrar deneme (aksi halde
                        // tarayıcı mutlak URL'yi encode eder; ham item.src ile asla eşleşmez → sonsuz onError).
                        if (item.thumbnail && item.src && !target.dataset.switchedToMain) {
                          target.dataset.switchedToMain = '1';
                          target.src = item.src;
                          return;
                        }
                        target.onerror = null;
                      }}
                    />
                  ) : (
                    <div className="library-item-placeholder">
                      {item.type === 'clip' ? '🎬' : '📷'}
                    </div>
                  )}
                  <span className="library-item-name">{item.name}</span>
                  <div className="library-item-actions absolute top-0 right-0 flex items-center gap-2">
                    <Button
                      variant="surface"
                      radius="none"
                      size="1"
                      onClick={() => handleDeleteLibraryItem(item)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="library-actions">
              <button className="btn" onClick={handleImportClick} disabled={isUploading}>
                {isUploading ? t('toolpads.library.uploading') : t('toolpads.library.import')}
              </button>
            </div>
          </>
        )}
        {isUploading ? (
          <div className="library-upload-overlay" role="status" aria-live="polite">
            <div className="library-upload-spinner" aria-hidden="true" />
            <span className="library-upload-label">
              {t('toolpads.library.uploading')}
              {uploadProgress > 0 ? ` ${uploadProgress}%` : ''}
            </span>
          </div>
        ) : null}
      </div>
      ) : (
        <div className="library-content">
          <p className="library-vector-hint">{t('toolpads.library.vectorHint')}</p>
          {vectorsLoading ? (
            <p className="library-vector-hint">{t('common.loading')}</p>
          ) : vectorFiles.length === 0 ? (
            <div className="library-empty">
              <p>{t('toolpads.library.vectorsEmpty')}</p>
            </div>
          ) : (
            <div className="library-grid">
              {vectorFiles.map((file) => (
                <div
                  key={file.id}
                  className="library-item library-item-vector"
                  title={file.name}
                  draggable
                  onDragStart={(event) => handleVectorDragStart(event, file)}
                  onDoubleClick={() => void handleUseVector(file)}
                >
                  <img src={resolveAssetUrl(file.url)} alt={file.name} />
                  <span className="library-item-name">{file.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Toolpad>
  );
}